import { forwardRef } from "react";
import * as React from "react";
import cn from "classnames";
import CloseButton, { CloseButtonProps } from "@ebsco-ui/close-button";
import { useModalContext } from "../modal-context";

export interface ModalHeaderCloseButtonProps
  extends React.HTMLAttributes<HTMLButtonElement> {
  /** className to be appended to the classnames for the top level element.
   * Provides BEM formatted override classname. */
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** className to be appended to the classnames for the top level element */
  className?: string;
  /** Props for the close button in the header. If none are provided, a close button will not be visible. */
  closeButtonProps?: Omit<CloseButtonProps, "onClick"> &
    ModalHeaderCloseButtonProps;
}

const ModalHeader = forwardRef<HTMLDivElement, ModalHeaderProps>(
  (
    { children, className: consumerClassname, closeButtonProps, ...rest },
    ref,
  ) => {
    const { getBlocksWith } = useModalContext();

    return (
      <div
        className={cn(consumerClassname, getBlocksWith("__header"))}
        data-auto="modal-header"
        ref={ref}
        {...rest}
      >
        <div className={getBlocksWith("__header-children")}>{children}</div>
        {closeButtonProps && (
          <CloseButton
            {...closeButtonProps}
            className={cn(
              closeButtonProps.className,
              "close-button",
              getBlocksWith("__close-button"),
            )}
            onClick={(ev) => {
              closeButtonProps.onClick && closeButtonProps.onClick(ev);
            }}
          />
        )}
      </div>
    );
  },
);

export default ModalHeader;
